import { Menu, MenuItem, MenuTrigger, Popover } from "react-aria-components";
import { cn, tv } from "tailwind-variants";
import { Button } from "#/_packages/umbrella/features/button/components/button.tsx";
import UmbSpriteIcon, {
	type SpriteIconSize,
} from "#/_packages/umbrella/features/icons/components/umb-sprite-icon.tsx";
import type { ButtonVariant, Intent, Size } from "#/_packages/umbrella/types";
import { m } from "#/paraglide/messages";

export type LanguageTextLabelVariant = "none" | "abbreviation" | "text";
export type LanguageIconPosition = "start" | "end";

export type LanguageItem = {
	code: string;
	label: string;
	abbreviation: string;
	href: string;
	isActive?: boolean;
	isDisabled?: boolean;
};

export type LanguageSelectorProps = {
	languages: readonly LanguageItem[];
	textLabelVariant?: LanguageTextLabelVariant;
	size?: Size;
	intentColor?: Intent;
	typeVariant?: ButtonVariant;
	icon?: string | null;
	iconPosition?: LanguageIconPosition;
	iconSize?: SpriteIconSize;
	className?: string;
	isPreview?: boolean;
};

const languageSelector = tv({
	slots: {
		root: "block__language-selector dropdown",
		button:
			"btn-language-selector inline-flex! items-center gap-1 dropdown-toggle capitalize",
		menu: "dropdown-menu dropdown-language-selector dropdown-menu-end bg-white",
		item: "dropdown-item block outline-none data-disabled:opacity-50 data-disabled:cursor-default",
	},
	variants: {
		size: {
			extraSmall: { button: "btn-xs" },
			small: { button: "btn-sm" },
			medium: { button: "" },
			large: { button: "btn-lg" },
			extraLarge: { button: "btn-xl" },
			fullWidth: { button: "btn-full-width" },
		},
		intentColor: {
			primary: { button: "btn-primary" },
			secondary: { button: "btn-secondary" },
			light: { button: "btn-light" },
			dark: { button: "btn-dark" },
			white: { button: "btn-white" },
			black: { button: "btn-black" },
			success: { button: "btn-success" },
			warning: { button: "btn-warning" },
			danger: { button: "btn-danger" },
			info: { button: "btn-info" },
			none: { button: "btn-none" },
		},
		typeVariant: {
			solid: { button: "" },
			outline: { button: "btn-outline" },
			ghost: { button: "btn-ghost" },
			soft: { button: "btn-soft" },
			link: { button: "btn-link" },
			quiet: { button: "quiet" },
			none: { button: "" },
		},
	},
	defaultVariants: {
		size: "medium",
		intentColor: "primary",
		typeVariant: "solid",
	},
});

function LanguageLabel({
	active,
	variant,
}: {
	active: LanguageItem;
	variant: LanguageTextLabelVariant;
}) {
	switch (variant) {
		case "none":
			return <span className="sr-only">{active.label}</span>;
		case "abbreviation":
			return (
				<>
					<span aria-hidden="true">{active.abbreviation}</span>
					<span className="sr-only">{active.label}</span>
				</>
			);
		case "text":
			return <span aria-hidden="true">{active.label}</span>;
	}
}

export function LanguageSelector({
	languages,
	textLabelVariant = "text",
	size = "medium",
	intentColor = "primary",
	typeVariant = "solid",
	icon,
	iconPosition = "start",
	iconSize = "medium",
	className,
	isPreview = false,
}: LanguageSelectorProps) {
	const active =
		languages.find((item) => item.isActive) ??
		languages[0] ??
		(isPreview
			? {
					code: "preview",
					label: m.language_selector(),
					abbreviation: m.language_selector(),
					href: "#",
				}
			: undefined);
	const menuLanguages = languages.filter((item) => item.code !== active?.code);
	const { root, button, menu, item } = languageSelector({
		size,
		intentColor,
		typeVariant,
	});
	if (!active) {
		return null;
	}

	const iconElement = icon ? (
		<UmbSpriteIcon icon={icon} iconSize={iconSize} />
	) : null;
	const trigger = (
		<Button
			type="button"
			isDisabled={!isPreview && menuLanguages.length === 0}
			className={button()}
			aria-label={m.language_selector()}
		>
			{iconPosition === "start" ? iconElement : null}
			<LanguageLabel active={active} variant={textLabelVariant} />
			{iconPosition === "end" ? iconElement : null}
		</Button>
	);

	if (isPreview || menuLanguages.length === 0) {
		return <div className={cn(root(), className)}>{trigger}</div>;
	}

	return (
		<div className={cn(root(), className)}>
			<MenuTrigger>
				{trigger}
				<Popover placement="bottom end" offset={4}>
					<Menu className={menu()}>
						{menuLanguages.map((language) => (
							<MenuItem
								key={language.code}
								{...(!language.isDisabled && language.href
									? { href: language.href }
									: {})}
								isDisabled={language.isDisabled}
								data-tracking-language={
									!language.isDisabled && language.href ? language.label : undefined
								}
								data-tracking-language-code={
									!language.isDisabled && language.href ? language.code : undefined
								}
								aria-label={language.label}
								className={item()}
							>
								{language.label}
							</MenuItem>
						))}
					</Menu>
				</Popover>
			</MenuTrigger>
		</div>
	);
}
